/**
 * The upload story, whole: sniff-gate (unsupported files never leave
 * the machine), web-ifc instant preview at t=0 (gray = local,
 * provisional), the org-bucket upload as THE ingest trigger (its
 * OBJECT_FINALIZE event feeds the worker's pull loop -- no backend in
 * the upload path), and live parse progress off the stream. The api
 * route survives only as the dev fallback: the compose stack runs
 * just the auth emulator, so the bucket upload no-ops there.
 * Completion needs no polling -- the 'parsed' event both ends the
 * progress AND invalidates the viewer's elements query through the
 * api bridge.
 *
 * The preview RENDERS elsewhere: this panel only elects the file and
 * hands it up through onPreview -- the shell owns the main canvas
 * slot and places IfcPreview there (modules never reach the frame).
 * 'parsed' hands up null: server truth replaces the provisional gray.
 */
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ACCENT, INK } from '../../../theme';
import { API_BASE, authFetch, subscribeStream } from '../../../api';
import { type IfcSniff, isSupportedSchema, sniffIfc } from '../sniff';
import { uploadToOrgBucket } from '../storage';

// Keyed by staging file: parse events are per-FILE (payload.file), and
// collapsing them into one counter mixes concurrent ingests into
// nonsense (seen live on staging: "512 / 256" -- one run's staged
// count over another run's total).
type IngestProgress = Map<string, { staged: number; total: number }>;

export function UploadPanel({
  projectId,
  onPreview,
}: {
  projectId: string;
  onPreview: (file: File | null) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [sniff, setSniff] = useState<IfcSniff | null>(null);
  const [progress, setProgress] = useState<IngestProgress>(new Map());

  useEffect(() => {
    return subscribeStream((event) => {
      if (event.payload.project_id !== projectId) return;
      const file = String(event.payload.file ?? '');
      if (event.kind === 'parsing') {
        setProgress((prev) =>
          new Map(prev).set(file, { staged: 0, total: Number(event.payload.elements ?? 0) }),
        );
      } else if (event.kind === 'streaming') {
        setProgress((prev) => {
          const staged = Number(event.payload.staged ?? 0);
          return new Map(prev).set(file, { staged, total: prev.get(file)?.total ?? staged });
        });
      } else if (event.kind === 'parsed') {
        setProgress(new Map());
        setUploading(false);
        onPreview(null);
      }
    });
  }, [projectId, onPreview]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileSniff = await sniffIfc(file);
    setSniff(fileSniff);
    if (!isSupportedSchema(fileSniff.schema)) {
      alert(`Unsupported IFC schema: ${fileSniff.schema ?? 'not an IFC file'}`);
      event.target.value = '';
      return;
    }

    // Every supported file previews -- meshing runs in a worker
    // (meshWorker.ts), so size no longer threatens the main thread;
    // the degrade path survives only for files web-ifc cannot parse.
    onPreview(file);

    setUploading(true);
    setUploadError(null);
    setProgress(new Map());

    // The org-bucket upload is the ingest trigger (project in the
    // PATH -- box 5's addressing contract; the subscription is live,
    // so the transitional dual-path is retired). Null in dev: the
    // api route below stays dev's trigger.
    const bucketUpload = uploadToOrgBucket(file, projectId);
    if (bucketUpload !== null) {
      // Refusals must surface -- the swallowed-verdict lesson (seen
      // live 2026-08-14 on the api path) applies to storage rules
      // too: an unauthorized write reads as silence otherwise.
      bucketUpload.on('state_changed', undefined, (err) => {
        console.error(err);
        setUploadError(err.message);
        setUploading(false);
      });
      event.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('files', file);
    formData.append('project_id', projectId);
    try {
      // fetch never throws on HTTP refusals -- the response is the
      // verdict, and a swallowed 403 reads as silence (seen live
      // 2026-08-14: "no progress" was a membershipless upload).
      const res = await authFetch(`${API_BASE}/v1/uploads/geospatial`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const detail = await res
          .json()
          .then((body: { detail?: unknown }) => body?.detail)
          .catch(() => null);
        setUploadError(
          res.status === 403
            ? 'This account has no organization yet — onboard first.'
            : typeof detail === 'string'
              ? detail
              : `upload refused: ${res.status}`,
        );
        setUploading(false);
      }
    } catch (e) {
      console.error(e);
      setUploadError(e instanceof Error ? e.message : String(e));
      setUploading(false);
    } finally {
      event.target.value = '';
    }
  };

  return (
    <>
      <label className="upload-btn">
        {uploading ? 'Ingesting...' : 'Upload IFC File'}
        <input type="file" accept=".ifc" onChange={handleFileUpload} style={{ display: 'none' }} />
        {sniff && (
          <span style={{ fontSize: '11px', color: INK.secondary }}>
            {sniff.schema} · {sniff.totalEntities.toLocaleString()} entities
          </span>
        )}
      </label>
      {uploadError && (
        <div style={{ marginTop: '0.5rem', fontSize: '13px', color: ACCENT.red }}>
          {uploadError}{' '}
          {uploadError.includes('onboard') && (
            <Link to="/onboard" style={{ color: ACCENT.red, textDecoration: 'underline' }}>
              Onboard →
            </Link>
          )}
        </div>
      )}
      {progress.size > 0 && (
        <div style={{ marginTop: '0.5rem', fontSize: '14px', color: ACCENT.amber }}>
          {Array.from(progress.entries()).map(([file, p]) => (
            <div key={file}>
              Parsing{progress.size > 1 ? ` ${file.split('/').pop()}` : ''}: {p.staged} / {p.total}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
